/**
 * AudioRecordingPreview - Preview de gravação de áudio
 * Exibe waveform animado durante gravação e player após conclusão
 */

import React from 'react';
import { Icon } from '@/shared/icons'
import { pauseOtherMedia } from '../utils/mediaPlayback';
interface AudioRecordingPreviewProps {
  /** Se está gravando */
  isRecording: boolean;
  /** Se está pausado (não usado mais, mantido para compatibilidade) */
  isPaused?: boolean;
  /** Duração formatada (MM:SS) */
  duration: string;
  /** URL do áudio gravado */
  audioUrl?: string;
  /** Callback ao parar */
  onStop?: () => void;
  /** Callback ao cancelar */
  onCancel?: () => void;
  /** Callback ao deletar */
  onDelete?: () => void;
  // Props removidas: onPause, onResume, onSend
}

/**
 * Componente de preview de gravação de áudio
 *
 * @example
 * ```tsx
 * <AudioRecordingPreview
 *   isRecording={true}
 *   duration="00:42"
 *   onStop={stopRecording}
 *   onCancel={cancelRecording}
 * />
 * ```
 */
const AudioRecordingPreview: React.FC<AudioRecordingPreviewProps> = ({
  isRecording,
  duration,
  audioUrl,
  onStop,
  onCancel,
  onDelete,
}) => {
  return (
    <div
      className="border-t p-4"
      style={{
        borderColor: 'var(--theme-card-border)',
        backgroundColor: 'var(--theme-card-background)'
      }}
    >
      <div className="flex items-center gap-3">
        {/* Recording Indicator / Audio Player */}
        {isRecording ? (
          <>
            {/* Waveform Animation */}
            <div
              className="flex items-center justify-center gap-1 w-12 h-12 rounded-full"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
            >
              <div
                className="w-1 rounded-full h-6 animate-pulse"
                style={{ backgroundColor: 'var(--theme-error-color)' }}
              />
              <div
                className="w-1 rounded-full h-8 animate-pulse"
                style={{
                  backgroundColor: 'var(--theme-error-color)',
                  animationDelay: '0.1s'
                }}
              />
              <div
                className="w-1 rounded-full h-6 animate-pulse"
                style={{
                  backgroundColor: 'var(--theme-error-color)',
                  animationDelay: '0.2s'
                }}
              />
            </div>

            {/* Duration */}
            <div className="flex-1">
              <p
                className="text-sm font-semibold"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Gravando áudio...
              </p>
              <p
                className="text-lg font-mono"
                style={{ color: 'var(--theme-error-color)' }}
              >
                {duration}
              </p>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2">
              {/* Stop */}
              <button
                onClick={onStop}
                className="p-2 rounded-lg transition-colors"
                style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  color: 'var(--theme-error-color)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                }}
                title="Parar gravação"
              >
                <Icon name="square" className="w-5 h-5" />
              </button>

              {/* Cancel */}
              <button
                onClick={onCancel}
                className="p-2 rounded-lg transition-colors"
                style={{
                  backgroundColor: 'var(--theme-button-ghost-hover)',
                  color: 'var(--theme-muted-text)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--theme-card-text)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--theme-muted-text)';
                }}
                title="Cancelar gravação"
              >
                <Icon name="x" className="w-5 h-5" />
              </button>
            </div>
          </>
        ) : audioUrl ? (
          <>
            {/* Audio Player */}
            <div className="flex-1">
              <audio controls className="w-full" src={audioUrl} onPlay={(e) => pauseOtherMedia(e.currentTarget)}>
                Seu navegador não suporta reprodução de áudio.
              </audio>
            </div>

            {/* Duration Badge */}
            <div
              className="px-3 py-1 rounded-lg"
              style={{
                backgroundColor: 'var(--theme-button-secondary)',
                color: 'var(--theme-button-secondary-text)'
              }}
            >
              <p className="text-sm font-mono">{duration}</p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Delete - Use o botão de enviar fixo para enviar o áudio */}
              <button
                onClick={onDelete}
                className="p-2 rounded-lg transition-colors"
                style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  color: 'var(--theme-error-color)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                }}
                title="Excluir áudio"
              >
                <Icon name="trash" className="w-5 h-5" />
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

AudioRecordingPreview.displayName = 'AudioRecordingPreview';

export default AudioRecordingPreview;
